/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$button-left-right-padding: 32px !default;
$button-small-left-right-padding: 24px !default;
$button-large-left-right-padding: 48px !default;
$button-height: 48px !default;
$button-small-height: 36px !default;
$button-large-height: 56px !default;
$button-font-size: 18px !default;
$button-small-font-size: 16px !default;
$button-large-font-size: 18px !default;
$button-line-height: 18px !default;
$button-small-line-height: 16px !default;
$button-large-line-height: 18px !default;
$button-primary-border-size: 0px !default;
$button-small-primary-border-size: 0px !default;
$button-large-primary-border-size: 0px !default;
$button-secondary-border-size: 3px !default;
$button-small-secondary-border-size: 3px !default;
$button-large-secondary-border-size: 3px !default;
$button-functional-border-size: 3px !default;
$button-small-functional-border-size: 3px !default;
$button-large-functional-border-size: 3px !default;
$button-shadow: none !default;
$button-small-shadow: none !default;
$button-large-shadow: none !default;
$button-radius: 14px !default;
$button-small-radius: 10px !default;
$button-large-radius: 16px !default;
$button-functional-radius: 4px !default;
$button-small-functional-radius: 4px !default;
$button-large-functional-radius: 4px !default;
$button-margin-icon-text: 12px !default;
$button-font-base-correction: 0px !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

$button-primary-padding: $button-left-right-padding - $button-primary-border-size;
$button-small-primary-padding: $button-small-left-right-padding - $button-small-primary-border-size;
$button-large-primary-padding: $button-large-left-right-padding - $button-large-primary-border-size;
$button-secondary-padding: $button-left-right-padding - $button-secondary-border-size;
$button-small-secondary-padding: $button-small-left-right-padding - $button-small-secondary-border-size;
$button-large-secondary-padding: $button-large-left-right-padding - $button-large-secondary-border-size;
$button-functional-padding: $button-left-right-padding - $button-functional-border-size;
$button-small-functional-padding: $button-small-left-right-padding - $button-small-functional-border-size;
$button-large-functional-padding: $button-large-left-right-padding - $button-large-functional-border-size;

@function capitalize($string) {
  @return to-upper-case(str-slice($string, 1, 1)) + str-slice($string, 2);
}

@mixin setButtonColors($rank, $state) {
  background-color: v(#{$rank} + "Background" + #{capitalize($state)} + "Color");
  border-color: v(#{$rank} + "Border" + #{capitalize($state)} + "Color");
  color: v(#{$rank} + "TextAndIcon" + #{capitalize($state)} + "Color");

  .#{$prefix}icon {
    fill: v(#{$rank} + "TextAndIcon" + #{capitalize($state)} + "Color");
  }
}

:host {
  box-sizing: border-box;
  display: inline-block;
}

:host,
[type="button"],
[type="reset"],
[type="submit"] {
  @include button-reset-appearance;
}

:host([disabled]:not([disabled="false"])) {
  pointer-events: none;
}

:host(.#{$prefix}full-width) {
  display: block;
}

a,
button {
  @include button-reset;
  font-family: $font-family-base;
  position: relative;
  width: auto;
  height: auto;
  min-height: $button-height;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  text-align: center;
  font-weight: bold;
  font-size: $button-font-size;
  font-weight: $font-weight-bold;
  line-height: $button-line-height;
  box-shadow: $button-shadow;
  border-radius: $button-radius;
  text-decoration: none;
  cursor: pointer;
  transition: background-color $transition-duration $transition-timing-function,
    color $transition-duration $transition-timing-function,
    border-color $transition-duration $transition-timing-function,
    opacity $transition-duration $transition-timing-function;
  @include noneUserSelect();

  /// hides the dashed inner focus line that FF draws. This is required.
  /* &::-moz-focus-inner {
    border: 0;
  } */

  .#{$prefix}icon {
    fill: v(primaryTextAndIconColor);
    transition: fill $transition-duration $transition-timing-function,
      stroke $transition-duration $transition-timing-function;

    &.#{$prefix}icon-left {
      margin-right: $button-margin-icon-text;
    }

    &.#{$prefix}icon-right {
      margin-left: $button-margin-icon-text;
    }
  }

  .#{$prefix}font-base-correction {
    padding: 4px 0px 4px 0px;
    margin-top: $button-font-base-correction;
  }

  &.#{$prefix}icon-only {
    .#{$prefix}icon {
      margin-right: 0;
      margin-left: 0;
    }
  }

  // Remove focus styles for non-keyboard focus. https://stackoverflow.com/a/60219624
  &:focus:not(:focus-visible) {
    outline: none !important;
  }

  &.#{$prefix}primary {
    padding-left: $button-primary-padding;
    padding-right: $button-primary-padding;
    background-color: v(primaryBackgroundColor);
    border: $button-primary-border-size solid v(primaryBorderColor);
    color: v(primaryTextAndIconColor);

    &.#{$prefix}icon-only {
      padding-left: calc(8px - $button-primary-border-size) !important;
      padding-right: calc(8px - $button-primary-border-size) !important;
    }

    &:focus {
      @include focusBorder(v(primaryFocusOutlineColor));
      @include setButtonColors("primary", "focus");
    }

    &:focus-within {
      @include focusBorder(v(primaryFocusOutlineColor));
      @include setButtonColors("primary", "focus");
    }

    &:hover {
      @include setButtonColors("primary", "hover");
    }

    &.#{$prefix}disabled {
      @include setButtonColors("primary", "disabled");
    }
  }

  &.#{$prefix}secondary {
    padding-left: $button-secondary-padding;
    padding-right: $button-secondary-padding;
    background-color: v(secondaryBackgroundColor);
    border: $button-secondary-border-size solid v(secondaryBorderColor);
    color: v(secondaryTextAndIconColor);

    &.#{$prefix}icon-only {
      padding-left: calc(8px - $button-secondary-border-size) !important;
      padding-right: calc(8px - $button-secondary-border-size) !important;
    }

    .#{$prefix}icon {
      fill: v(secondaryTextAndIconColor);
    }

    &:focus {
      @include focusBorder(v(secondaryFocusOutlineColor));
      @include setButtonColors("secondary", "focus");
    }

    &:focus-within {
      @include focusBorder(v(primaryFocusOutlineColor));
      @include setButtonColors("secondary", "focus");
    }

    &:hover {
      @include setButtonColors("secondary", "hover");
    }

    &.#{$prefix}disabled {
      @include setButtonColors("secondary", "disabled");
    }
  }

  &.#{$prefix}functional {
    padding-left: $button-functional-padding;
    padding-right: $button-functional-padding;
    background-color: v(functionalBackgroundColor);
    border: $button-functional-border-size solid v(functionalBorderColor);
    border-radius: $button-functional-radius;
    color: v(functionalTextAndIconColor);

    &.#{$prefix}icon-only {
      padding-left: calc(8px - $button-functional-border-size) !important;
      padding-right: calc(8px - $button-functional-border-size) !important;
    }

    .#{$prefix}icon {
      fill: v(functionalTextAndIconColor);
    }

    &:focus {
      @include focusBorder(v(functionalFocusOutlineColor));
      @include setButtonColors("functional", "focus");
    }

    &:focus-within {
      @include focusBorder(v(functionalFocusOutlineColor));
      @include setButtonColors("functional", "focus");
    }

    &:hover {
      @include setButtonColors("functional", "hover");
    }

    &.#{$prefix}disabled {
      @include setButtonColors("functional", "disabled");
    }
  }

  /** Small button **/
  &.#{$prefix}small {
    min-height: $button-small-height;
    line-height: $button-small-line-height;
    box-shadow: $button-small-shadow;
    border-radius: $button-small-radius;
    font-size: $button-small-font-size;

    &.#{$prefix}primary {
      padding-left: $button-small-primary-padding;
      padding-right: $button-small-primary-padding;
      border-width: $button-small-primary-border-size;

      &.#{$prefix}icon-only {
        padding-left: calc(6px - $button-small-primary-border-size) !important;
        padding-right: calc(6px - $button-small-primary-border-size) !important;
      }
    }

    &.#{$prefix}secondary {
      padding-left: $button-small-secondary-padding;
      padding-right: $button-small-secondary-padding;
      border-width: $button-small-secondary-border-size;

      &.#{$prefix}icon-only {
        padding-left: calc(6px - $button-small-secondary-border-size) !important;
        padding-right: calc(6px - $button-small-secondary-border-size) !important;
      }
    }

    &.#{$prefix}functional {
      padding-left: $button-small-functional-padding;
      padding-right: $button-small-functional-padding;
      border-width: $button-small-functional-border-size;
      border-radius: $button-small-functional-radius;

      &.#{$prefix}icon-only {
        padding-left: calc(6px - $button-small-functional-border-size) !important;
        padding-right: calc(6px - $button-small-functional-border-size) !important;
      }
    }
  }

  /** Large button **/
  &.#{$prefix}large {
    min-height: $button-large-height;
    line-height: $button-large-line-height;
    box-shadow: $button-large-shadow;
    border-radius: $button-large-radius;
    font-size: $button-large-font-size;

    &.#{$prefix}primary {
      padding-left: $button-large-primary-padding;
      padding-right: $button-large-primary-padding;
      border-width: $button-large-primary-border-size;

      &.#{$prefix}icon-only {
        padding-left: calc(12px - $button-large-primary-border-size) !important;
        padding-right: calc(12px - $button-large-primary-border-size) !important;
      }
    }

    &.#{$prefix}secondary {
      padding-left: $button-large-secondary-padding;
      padding-right: $button-large-secondary-padding;
      border-width: $button-large-secondary-border-size;

      &.#{$prefix}icon-only {
        padding-left: calc(12px - $button-large-secondary-border-size) !important;
        padding-right: calc(12px - $button-large-secondary-border-size) !important;
      }
    }

    &.#{$prefix}functional {
      padding-left: $button-large-functional-padding;
      padding-right: $button-large-functional-padding;
      border-width: $button-large-functional-border-size;
      border-radius: $button-large-functional-radius;

      &.#{$prefix}icon-only {
        padding-left: calc(12px - $button-large-functional-border-size) !important;
        padding-right: calc(12px - $button-large-functional-border-size) !important;
      }
    }
  }

  &.#{$prefix}full-width {
    width: 100%;
  }

  @include for-size(phone) {
    &.#{$prefix}full-width-mobile {
      width: 100%;
    }
  }

  eon-ui-preloader-squares {
    z-index: 1;
  }
}

a {
  &:focus {
    outline: none;
  }

  &:focus-within {
    outline: none;
  }
}
